import { Component, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { DateTime as LuxonDateTime } from 'luxon';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { FormBuilder, Validators } from '@angular/forms';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from './../../shared/providers/tidy-storage';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';
import { Loading } from 'src/shared/components/loading/loading';
import {
  GuestReservationResponse,
  Job,
  Reservation,
  ReservationGuest,
} from 'src/models/schedule-weekly-view.model';
import {
  ChannelBreakdownView,
  MINIMUM_RESERVATION_PRICE_CENTS,
  TIDY_LISTING_SOURCE_SLUG,
  UpdateGuestReservation,
  channelBreakdownView,
  isHostAddedSource,
  reservationCurrency,
  reservationHostPayoutCents,
  reservationPriceCents,
} from 'src/models/guest-reservations';
import { centsToDollarsInput, dollarsToCents, formatCents } from 'src/shared/util/money';
import { validateMinimumPrice } from 'src/shared/validator/validateMinimumPrice';
import { Util } from 'src/shared/util/util';
import { PastJobPage } from '../more/past-job/past-job';
import { PastPrivateJobPage } from '../more/past-private-job/past-private-job';
import { JobPage } from '../schedule/job/job';
import { Schedule } from 'src/providers/schedule/schedule';
import { SuccessPage } from 'src/shared/pages/success/success';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { Integrations } from 'src/providers/integrations/integrations';
import { Client } from 'src/providers/client/client';
import { ModalController } from '@ionic/angular';
import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';
import { CustomFieldsPage } from '../more/custom-fields/custom-fields';
import { AddCustomFieldPage } from '../integrations/integration-settings/add-custom-field/add-custom-field';
import { WindowService } from 'src/shared/providers/window.service';

import { ReservationInteractionService } from 'src/pages/schedule/services/reservation-interaction.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { InquiriesService } from 'src/shared/services/inquiries.service';
import { ReservationLockCode, ReservationLockCodesService } from 'src/shared/services/reservation-lock-codes.service';
import {
  manualAccessCode,
  ReservationAccessCode,
  ReservationAccessCodeService,
} from 'src/shared/services/reservation-access-code.service';
import { InquiryDeclineSubmit } from 'src/shared/components/inquiry-decline-modal/inquiry-decline-modal.component';

import {
  Inquiry,
  approveBlockedMessage,
  cardFailureReasonLabel,
  parseInquiryActionError,
} from 'src/models/inquiry.model';
import { InquiryStatusTone, inquiryStatusPresentation } from 'src/shared/util/inquiry-status';

// Pre-computed display model for the inquiry variant of this modal, so the
// template only binds strings.
interface InquiryViewModel {
  guestName: string;
  initials: string;
  subtitle: string;
  statusLabel: string;
  statusTone: InquiryStatusTone;
  // Expired rows only: which way it expired. See inquiryStatusDetail.
  statusDetail: string | null;
  needsResponse: boolean;
  bannerTitle: string;
  bannerHint: string;
  // Tidy: the host approved but the guest's card was refused — approve retries.
  chargeFailedNote: string | null;
  checkIn: ReservationStayEdge;
  checkOut: ReservationStayEdge;
  nightsLabel: string | null;
  guestsLabel: string | null;
  propertyLine: string;
  expiresLabel: string | null;
  message: string | null;
  payoutTitle: string;
  payoutHint: string;
  breakdownRows: { label: string; value: string }[];
  totalLabel: string | null;
  canPreapprove: boolean;
  // Tidy accept-and-charge (POST /inquiries/:id/approve).
  canApprove: boolean;
  acceptLabel: string;
  canDecline: boolean;
  canAct: boolean;
  platformUrl: string | null;
  platformLinkText: string;
}

// One end of the stay itinerary strip (check-in or check-out column).
interface ReservationStayEdge {
  weekday: string;
  date: string;
}

// A guest row with its avatar initials precomputed (no template functions).
interface ReservationGuestRow {
  id: number;
  name: string;
  initials: string;
  email: string | null;
  phone: string | null;
  isBooker: boolean;
}

// Pre-computed display model for the reservation variant, so its card shares
// the polished guest-forward layout the inquiry uses (avatar, itinerary strip,
// pills) and the template only binds strings.
interface ReservationViewModel {
  guestName: string;
  initials: string;
  subtitle: string;
  checkIn: ReservationStayEdge;
  checkOut: ReservationStayEdge;
  nightsLabel: string | null;
  guestsLabel: string | null;
  guestBreakdown: string | null;
  guestRows: ReservationGuestRow[];
  addedByYou: boolean;
  sourceName: string | null;
  // A Vrbo hold: the guest hasn't confirmed the stay yet.
  isHold: boolean;
  autoScheduleStatuses: string[];
  // What the guest paid for the whole stay, formatted: the host's own price,
  // or the platform's total on a synced stay. Null when nobody has priced it.
  priceLabel: string | null;
  // Only a synced stay reports one.
  payoutLabel: string | null;
  // A synced Airbnb/Vrbo stay's line items, shown between the two totals.
  breakdown: ChannelBreakdownView | null;
  // Only a reservation the host keeps themselves can be repriced; a synced
  // one's money belongs to its integration.
  canEditPrice: boolean;
  // Tidy reservations cancel-and-refund instead of plain delete.
  deleteLabel: string;
}

@Component({
    templateUrl: 'reservation.html',
    styleUrls: ['./reservation.scss'],
    animations: [
        trigger('slideAnimation', [
            state('collapsed', style({
                height: '0px',
                overflow: 'hidden',
                opacity: '0'
            })),
            state('expanded', style({
                height: '*',
                overflow: 'visible',
                opacity: '1'
            })),
            transition('collapsed <=> expanded', [
                animate('300ms ease-in-out')
            ])
        ]),
        trigger('expandInOut', [
            transition(':enter', [
                style({ height: '0px', opacity: 0, overflow: 'hidden' }),
                animate('250ms ease-out', style({ height: '*', opacity: 1 }))
            ]),
            transition(':leave', [
                style({ overflow: 'hidden' }),
                animate('200ms ease-in', style({ height: '0px', opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class ReservationPage implements OnInit {

  address: any;
  guestReservation: GuestReservationResponse;
  detail: any;
  reservationDetails: any;
  hasReservationDetails = false;
  dialogParams: any;
  isRightSideContent = true;
  loaded: boolean;
  blockingJobs: any[] = [];
  calendarReservations: any[] = [];
  isLoadingBlockedData = false;
  automaticBookingSettings: any;
  customFields: any[] = [];
  expandedSourceData: { [key: string]: boolean } = {};
  isEditingTimes = false;
  hasTimeChanges = false;
  isEditingPrice = false;
  hasPriceChanges = false;
  priceSubmitted = false;
  timesItems = this.getAllTimeItems();

  // The schedule opens this same modal for inquiries; when true we fetch the
  // inquiry (not a reservation) and render the inquiry layout instead.
  isInquiry = false;
  inquiryDetail: Inquiry | null = null;
  inquiryView: InquiryViewModel | null = null;
  reservationView: ReservationViewModel | null = null;
  inquiryBusy = false;
  decliningInquiry: Inquiry | null = null;
  lockCodes: ReservationLockCode[] = [];
  // The Door Code card waits for the lock lookup so it never flashes "no lock".
  doorCodeReady = false;
  hasSmartLock = false;
  manualCode: ReservationAccessCode | null = null;
  isEditingDoorCode = false;
  doorCodeSubmitted = false;
  isSavingDoorCode = false;

  private readonly reservationInteraction = inject(ReservationInteractionService);
  private readonly inquiries = inject(InquiriesService);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly reservationLockCodes = inject(ReservationLockCodesService);
  private readonly reservationAccessCodes = inject(ReservationAccessCodeService);
  private readonly fb = inject(FormBuilder);

  editTimesForm = this.fb.group({
    checkInTime: this.fb.control<string | null>(null),
    checkOutTime: this.fb.control<string | null>(null),
  });

  editPriceForm = this.fb.group({
    price: this.fb.control<string | null>(null, validateMinimumPrice(MINIMUM_RESERVATION_PRICE_CENTS)),
  });

  doorCodeForm = this.fb.group({
    code: this.fb.control('', [Validators.required, Validators.maxLength(20), Validators.pattern(/^\s*[0-9A-Za-z#*]+\s*$/)]),
  });

  boundSaveTimes = () => this.saveTimes();
  boundSaveDoorCode = () => this.saveDoorCode();
  boundSavePrice = () => this.savePrice();

  constructor(
    private guestReservations: GuestReservations,
    private navCtrl: CustomNavController,
    private storage: TidyStorage,
    public utils: Util,
    private rightSidePanelService: RightSidePanelService,
    private schedule: Schedule,
    private integrations: Integrations,
    private client: Client,
    private modalController: ModalController,
    private windowService: WindowService
  ) {
    this.editTimesForm.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        this.hasTimeChanges =
          value.checkInTime !== this.normalizeTime(this.guestReservation?.check_in_time) ||
          value.checkOutTime !== this.normalizeTime(this.guestReservation?.check_out_time);
      });

    this.editPriceForm.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        this.hasPriceChanges = dollarsToCents(value.price) !== this.storedPriceCents;
      });
  }

  async ngOnInit() {
    this.isRightSideContent =
      (await this.storage.retrieve('dialog-right-side-open')) || false;
    this.dialogParams = await this.storage.retrieve('dialog-params');
    this.isInquiry =
      this.dialogParams?.isInquiry === true ||
      this.dialogParams?.guestReservation?.type === 'inquiry' ||
      this.dialogParams?.guestReservation?.state === 'inquiry';
    this.rightSidePanelService.setDialogPageTitle(this.isInquiry ? 'Inquiry' : 'Reservation');
    this.loadDataAndParams();
  }

  @Loading('', true)
  async loadDataAndParams() {
    try {
      this.loaded = false;
      this.guestReservation =
        this.dialogParams?.guestReservation ||
        (await this.navCtrl.getParam('guestReservation', true));
      this.reservationDetails = {
        total_guests: this.guestReservation?.total_guests,
        total_adults: this.guestReservation?.total_adults,
        total_children: this.guestReservation?.total_children,
        total_pets: this.guestReservation?.total_pets,
        total_infants: this.guestReservation?.total_infants,
      }
      this.hasReservationDetails =
        !!this.reservationDetails &&
        Object.keys(this.reservationDetails).length > 0;
      const id = this.guestReservation.id;
      if (this.isInquiry) {
        // Inquiries are their own resource: fetch the inquiry and stop. The
        // reservation-only enrichment (custom fields, overlaps, blocked jobs,
        // auto-booking source) doesn't apply to an inquiry.
        this.inquiryDetail = await firstValueFrom(this.inquiries.getInquiryById(id));
        this.detail = this.inquiryDetail;
        this.inquiryView = this.buildInquiryView(this.inquiryDetail);
        this.loaded = true;
        return;
      }
      // `address` is only a label and a hint for the overlap / blocked-job
      // lookups below, so a missing one must not redirect. getParam's
      // redirectOnNull would send the app to navigateRoot('') — the Overview
      // page — mid-load, which is what happened from the Schedule Day view.
      this.address =
        this.dialogParams?.address ||
        this.navCtrl.getParam('address');
      this.detail = await this.guestReservations.getReservationById(id);
      this.customFields = await this.getCustomFields(this.detail);
      this.checkForOverlappedReservations();
      this.checkForBlockedJobs();
      this.convertDatesIntoISO();
      this.reservationView = this.buildReservationView();
      this.loaded = true;
      this.loadLockCodes();
      this.automaticBookingSettings = await this.guestReservations.getReservationSource();
    } catch (_err) {
      // Intentionally empty
    }
  }

  convertDatesIntoISO() {
    const parseDate = (dateStr: string): string => {
      if (!dateStr) {return '';}
      // A bare YYYY-MM-DD is a calendar day. `new Date` reads it as midnight UTC, which west
      // of UTC renders as the day before, so read it as local midnight instead.
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return LuxonDateTime.fromISO(dateStr).toISO();
      }
      // Already a full ISO timestamp: keep the instant it names.
      if (dateStr.includes('-')) {
        return new Date(dateStr).toISOString();
      }
      // Handle "MMM DD, YYYY" format (e.g., "Mar 02, 2025")
      const parsed = new Date(dateStr);
      if (!isNaN(parsed.getTime())) {
        return parsed.toISOString();
      }
      return dateStr; // Return original if parsing fails
    };
    this.guestReservation.check_in_date = parseDate(this.guestReservation.check_in_date);
    this.guestReservation.check_out_date = parseDate(this.guestReservation.check_out_date);
  }

  async getCustomFields(detail: any) {
    try {
      const userCustomFields = await this.integrations.getCustomFields('GuestReservation');
      const customFields = detail?.custom_fields;
      return Object.keys(customFields).map((key) => {
        const userCustomField = userCustomFields.find((field) => field.field_key === key);
        return {
          name: userCustomField.field_name,
          value: customFields[key],
        };
      });
    } catch (_err) {
      return [];
    }
  }

  async checkForOverlappedReservations(): Promise<void> {
    try {
      const foundOverlappedReservations: any[] =
        this.detail?.automatic_booking_statuses?.filter(
          (status) => status?.metadata?.overlapped_by_guest_reservation
        ).map(
          (status) => status?.metadata?.overlapped_by_guest_reservation
        ).flat() ?? [];
      if (foundOverlappedReservations.length > 0) {
        this.isLoadingBlockedData = true;
        const reservationPromises = foundOverlappedReservations.map(async (overlappedData) => {
          const checkInDate = overlappedData.check_in_date;
          const checkOutDate = overlappedData.check_out_date;
          const calendarResponse = await this.schedule.getScheduleCalendar(
            checkInDate,
            checkOutDate,
            {},
            'reservations'
          );
          const calendarReservations = calendarResponse
            .map((items) => items.reservations)
            .flat();
          return this.schedule.parseCalendarReservations(
            calendarReservations,
            this.address,
            { startDate: checkInDate, endDate: checkOutDate },
            []
          ).filter(
            (reservation) =>
              overlappedData?.id === reservation?.id
          );
        });
        const allParsedReservations = await Promise.all(reservationPromises);
        this.calendarReservations = allParsedReservations.flat();
        this.isLoadingBlockedData = false;
      }
    } catch (err) {
      this.isLoadingBlockedData = false;
      this.utils.showError(`Error checking for overlapped reservations ${  err}`);
    }
  }

  async checkForBlockedJobs(): Promise<void> {
    try {
      const foundBlockedByOtherJob =
      this.detail?.automatic_booking_statuses?.find(
        (status) => status?.key === 'blocked_by_other_job'
      );
      if (foundBlockedByOtherJob) {
        this.isLoadingBlockedData = true;
        const checkInDate = this.guestReservation.check_in_date;
        const checkOutDate = this.guestReservation.check_out_date;
        const calendarResponse = await this.schedule.getScheduleCalendar(
          checkInDate,
          checkOutDate,
          {},
          'jobs'
        );
        const calendarJobs = calendarResponse.map((items) => items.jobs).flat();
        const parsedJobs = this.schedule.parseCalendarJobs(
          calendarJobs,
          this.address
        );
        this.blockingJobs = parsedJobs.filter((job) =>
          foundBlockedByOtherJob?.metadata?.booking_ids?.includes(
            job?.booking?.id
          )
        );
      }
      this.isLoadingBlockedData = false;
    } catch (err) {
      this.isLoadingBlockedData = false;
      this.utils.showError(new TranslationPipe().transform(`Error checking for blocked jobs ${  err}`));
    }
  }

  getUniqueStatuses(statuses: any[]): string[] {
    return statuses?.filter((status, index, self) =>
      index === self.findIndex((t) => t?.text === status?.text)
    ) || [];
  }

  goToJobPage(cleaning: any) {
    const addressId = cleaning?.address_id;
    const isMobileResolution = !this.windowService.isDesktopRes;
    const isPastJob =
      cleaning.status.name === 'cancelled' ||
      cleaning.status.name === 'completed';
    const isPrivateJob = cleaning.isPrivate;
    if (!isMobileResolution) {
      this.navigateToJobPageInsideDialog(
        cleaning,
        addressId,
        isPastJob,
        isPrivateJob
      );
      return;
    }
    localStorage.setItem('pastJobBackPage', 'schedule');
    if (isPastJob) {
      if (isPrivateJob) {
        this.navCtrl.navigateForward([`past-private-job/${cleaning.id}`]);
      } else {
        this.navCtrl.navigateForward([`past-job/${cleaning.id}`]);
      }
    } else {
      this.navCtrl.navigateForward([`job/${addressId}/${cleaning.id}`], {
        cleaning,
        object: cleaning.object,
      });
    }
  }

  navigateToJobPageInsideDialog(
    cleaning: Job,
    addressId: number,
    isPastJob: boolean,
    isPrivateJob: boolean
  ): void {
    const params = {
      jobId: cleaning.id,
      addressId,
      ...cleaning,
    };
    this.storage.save('dialog-params', params);
    this.storage.save('dialog-right-side-open', true);
    if (isPastJob && isPrivateJob) {
      this.rightSidePanelService.navigateTo(
        `past-private-job/${cleaning.id}`,
        params,
        PastPrivateJobPage
      );
    } else if (isPastJob) {
      this.rightSidePanelService.navigateTo(
        `past-job/${cleaning.id}`,
        params,
        PastJobPage
      );
    } else {
      this.rightSidePanelService.navigateTo(
        `job/${addressId}/${cleaning.id}`,
        params,
        JobPage
      );
    }
  }

  async goToReservation(reservation: any) {
    try {
      const guestReservation = {
        ...reservation,
        check_in_date: reservation?.object?.check_in_date,
        check_out_date: reservation?.object?.check_out_date,
        check_in_time: reservation?.object?.check_in_time,
        check_out_time: reservation?.object?.check_out_time,
      };
      const params = {
        guestReservation,
        address: this.address,
      };
      // INFO: This is a workaround to navigate to the reservation page on mobile
      // because the navigation between same pages is not working as expected
      const currentParams = await this.storage.retrieve('dialog-params') || await this.navCtrl.getParams();
      const dialogParams = {
        ...currentParams,
        ...params,
      };
      this.storage.save('dialog-params', dialogParams);
      this.ngOnInit();
    } catch (_err) {
      // Intentionally empty
    }
  }

  goToIntegrations() {
    this.navCtrl.navigateForward('integration/all');
  }

  // The stay's Seam smart-lock codes. Loaded after the main detail so a Seam
  // hiccup can't hold up the page.
  private async loadLockCodes(): Promise<void> {
    this.manualCode = manualAccessCode(this.detail);
    try {
      this.lockCodes = [];
      const reservationId = this.guestReservation?.id;
      const addressId = Number(
        this.dialogParams?.addressId ||
        this.navCtrl.getParam('addressId') ||
        (this.guestReservation as any)?.address_id ||
        this.detail?.address_id ||
        this.detail?.address?.id
      );
      if (!reservationId || !addressId) {
        return;
      }
      this.hasSmartLock = (await this.reservationLockCodes.getAssignedLocks(addressId)).length > 0;
      this.lockCodes = await this.reservationLockCodes.getLockCodes(reservationId, addressId);
    } catch (_err) {
      // No Seam integration or the device list failed — read as no lock.
    } finally {
      this.doorCodeReady = true;
    }
  }

  startEditDoorCode(): void {
    this.doorCodeForm.reset({ code: this.manualCode?.code ?? '' });
    this.doorCodeSubmitted = false;
    this.isEditingDoorCode = true;
  }

  cancelEditDoorCode(): void {
    this.isEditingDoorCode = false;
  }

  async saveDoorCode(): Promise<void> {
    if (this.demoBlock.block()) {
      return;
    }
    this.doorCodeSubmitted = true;
    if (this.doorCodeForm.invalid || this.isSavingDoorCode) {
      return;
    }
    const code = (this.doorCodeForm.value.code ?? '').trim();
    this.isSavingDoorCode = true;
    try {
      this.manualCode = await this.reservationAccessCodes.setManualCode(this.guestReservation.id, code);
      this.isEditingDoorCode = false;
      this.utils.showSuccess('Code saved. Guests will see it in their guidebook.');
    } catch (_err) {
      this.utils.showError('Couldn’t save the code. Please try again.');
    } finally {
      this.isSavingDoorCode = false;
    }
  }

  async removeDoorCode(): Promise<void> {
    if (this.demoBlock.block()) {
      return;
    }
    try {
      await this.reservationAccessCodes.clearManualCode(this.guestReservation.id);
      this.manualCode = null;
      this.isEditingDoorCode = false;
      this.utils.showSuccess('Code removed.');
    } catch (_err) {
      this.utils.showError('Couldn’t remove the code. Please try again.');
    }
  }

  copyLockCode(code: string): void {
    this.utils.copyToClipBoard(code);
    this.utils.showSuccess('Code copied to clipboard.');
  }

  openGuidebook() {
    // uuid can live on either the lightweight guestReservation passed in or the
    // richer detail fetched by id; surface whichever exists so the service can
    // build the public guidebook URL.
    const uuid = this.guestReservation?.uuid || this.detail?.uuid;
    this.reservationInteraction.openGuidebook({ ...this.guestReservation, uuid } as any, {
      address: this.address,
      addressId: this.dialogParams?.addressId || this.navCtrl.getParam('addressId'),
    });
  }

  // A direct-booking ("tidy") reservation was created by approving a tidy
  // inquiry: the /details payload carries `source_data.inquiry_id` (the only
  // key the backend writes there) and `source_type_name: 'Tidy'`. DELETE on it
  // refunds the guest in full, so the cancel copy must say so.
  private get isTidyReservation(): boolean {
    const detail = this.detail;
    if (detail?.source_data?.inquiry_id !== undefined && detail?.source_data?.inquiry_id !== null) {
      return true;
    }
    // By slug: a CSV upload's source type is named 'Tidy' too.
    const sourceTypeSlug = detail?.source_type_slug ?? this.guestReservation?.guest_reservation_source_type?.slug;
    return sourceTypeSlug === TIDY_LISTING_SOURCE_SLUG;
  }

  async deleteReservation(): Promise<void> {
    if (this.demoBlock.block()) {
      return;
    }
    const isTidy = this.isTidyReservation;
    const confirmParams: ConfirmPageParamsModel = isTidy
      ? {
          title: new TranslationPipe().transform('Cancel reservation?'),
          body: new TranslationPipe().transform(
            'This cancels the stay, issues the guest a full refund (processed on the nightly billing run), reopens the dates, and emails the guest.'
          ),
          backText: new TranslationPipe().transform('Go Back'),
          confirmText: new TranslationPipe().transform('Cancel & Refund'),
          confirmAction: this.confirmDeleteReservation.bind(this)
        }
      : {
          title: new TranslationPipe().transform('Delete Reservation?'),
          body: new TranslationPipe().transform('Are you sure you want to delete this reservation?'),
          backText: new TranslationPipe().transform('Cancel'),
          confirmText: new TranslationPipe().transform('Delete'),
          confirmAction: this.confirmDeleteReservation.bind(this)
        };
    const confirmationModal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: confirmParams,
      animated: false,
      cssClass: 'confirm-modal'
    });
    confirmationModal.present();
  }

  async confirmDeleteReservation(): Promise<void> {
    const isTidy = this.isTidyReservation;
    await this.guestReservations.delete(this.guestReservation.id);
    this.utils.showSuccess(
      isTidy ? 'Reservation cancelled — refund issued.' : 'Reservation deleted successfully'
    );
    this.modalController.dismiss();
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateRoot('more/reservations');
    }
  }

  getAllTimeItems() {
    return Array.from({ length: 48 }, (_, index) => {
      const luxonDateTimeObject = LuxonDateTime
        .now()
        .startOf('day')
        .plus({ minutes: index * 30 });
      const hour12Format = luxonDateTimeObject.toFormat('h:mma').toLowerCase();
      const militaryHourFormat = luxonDateTimeObject.toFormat('HH:mm');
      return { viewValue: hour12Format, value: militaryHourFormat };
    });
  }

  // API times may include seconds ("14:00:00"); select option values are "HH:mm"
  normalizeTime(time: string | null | undefined): string | null {
    return time ? time.slice(0, 5) : null;
  }

  startEditTimes(): void {
    this.editTimesForm.patchValue({
      checkInTime: this.normalizeTime(this.guestReservation?.check_in_time),
      checkOutTime: this.normalizeTime(this.guestReservation?.check_out_time),
    });
    this.isEditingTimes = true;
  }

  cancelEditTimes(): void {
    this.isEditingTimes = false;
  }

  startEditPrice(): void {
    this.editPriceForm.patchValue({ price: centsToDollarsInput(this.storedPriceCents) });
    this.priceSubmitted = false;
    this.isEditingPrice = true;
  }

  cancelEditPrice(): void {
    this.isEditingPrice = false;
  }

  // A cleared field sends null, which the API reads as "this reservation has
  // no price" and drops the revenue line with it.
  async savePrice(): Promise<void> {
    if (this.demoBlock.block()) {
      return;
    }
    this.priceSubmitted = true;
    if (this.editPriceForm.invalid) {
      return;
    }
    // dollarsToCents already reads $0.00 as null, so this is what the API stores.
    const amount = dollarsToCents(this.editPriceForm.value.price);
    try {
      await this.guestReservations.update(this.guestReservation.id, {
        guest_reservation_id: this.guestReservation.id,
        amount,
      });
      // Both copies: storedPriceCents falls back to the detail, so a cleared
      // price would otherwise read back as the one the modal opened with.
      this.guestReservation.amount = amount;
      if (this.detail) {
        this.detail.amount = amount;
      }
      this.isEditingPrice = false;
      this.reservationView = this.buildReservationView();
      localStorage.setItem('madeUpdateToSchedule', 'true');
      this.schedule.setReloadSchedulePage();
      this.utils.showSuccess(amount === null ? 'Reservation price removed' : 'Reservation price updated');
    } catch (err) {
      const errorMessage = err?.error?.message
        || err?.message
        || 'Error updating reservation, please try again.';
      this.utils.showError(errorMessage);
    }
  }

  // The price as stored, in cents. The modal is opened from the Reservations
  // list and from the Schedule, which carry it on the reservation itself; the
  // details fetch is the fallback for any caller that doesn't.
  private get storedPriceCents(): number | null {
    return this.guestReservation?.amount ?? this.detail?.amount ?? null;
  }

  async saveTimes(): Promise<void> {
    if (this.demoBlock.block()) {
      return;
    }
    const payload: UpdateGuestReservation = {
      guest_reservation_id: this.guestReservation.id,
      check_in_time: this.editTimesForm.value.checkInTime,
      check_out_time: this.editTimesForm.value.checkOutTime,
    };
    try {
      await this.guestReservations.update(this.guestReservation.id, payload);
      this.guestReservation.check_in_time = payload.check_in_time;
      this.guestReservation.check_out_time = payload.check_out_time;
      this.isEditingTimes = false;
      localStorage.setItem('madeUpdateToSchedule', 'true');
      this.schedule.setReloadSchedulePage();
      this.utils.showSuccess('Reservation times updated');
    } catch (err) {
      const errorMessage = err?.error?.message
        || err?.message
        || 'Error updating reservation, please try again.';
      this.utils.showError(errorMessage);
    }
  }

  get guests(): any[] {
    return this.detail?.guests ?? [];
  }

  get primaryGuest(): any | undefined {
    const guests = this.guests;
    return guests.find((guest) => guest.is_booker || guest.isBooker)
      ?? guests.find((guest) => guest.is_staying || guest.isStaying)
      ?? guests[0];
  }

  get canMessageGuest(): boolean {
    const guest = this.primaryGuest;
    return Boolean(guest?.phone || guest?.chat_room_key || guest?.chatRoomKey);
  }

  async messageGuest(): Promise<void> {
    await this.reservationInteraction.openMessageThread(this.reservationForMessaging);
  }

  private get reservationForMessaging(): Reservation {
    const guest = this.primaryGuest;
    const guests: ReservationGuest[] = this.guests.map((item) => ({
      id: item.id,
      name: item.name,
      email: item.email,
      phone: item.phone,
      platform: item.platform,
      isBooker: item.is_booker ?? item.isBooker,
      isStaying: item.is_staying ?? item.isStaying,
      chatRoomKey: item.chat_room_key || item.chatRoomKey || null,
    }));
    return {
      ...(this.guestReservation as any),
      ...(this.detail as any),
      id: this.guestReservation?.id || this.detail?.id,
      title: 'Reservation',
      guests,
      guestName: guest?.name || this.guestReservation?.guest_name || this.detail?.guest_name,
      phone: guest?.phone || this.guestReservation?.guest_phone || this.detail?.guest_phone || this.guestReservation?.phone || this.detail?.phone,
    } as Reservation;
  }

  getGuestInfoString(): string {
    const parts: string[] = [];
    if (this.reservationDetails?.total_guests) {
      parts.push(`${this.reservationDetails.total_guests} guests`);
    }
    if (this.reservationDetails?.total_adults) {
      parts.push(`${this.reservationDetails.total_adults} adults`);
    }
    if (this.reservationDetails?.total_children) {
      parts.push(`${this.reservationDetails.total_children} children`);
    }
    if (this.reservationDetails?.total_pets) {
      parts.push(`${this.reservationDetails.total_pets} pets`);
    }
    if (this.reservationDetails?.total_infants) {
      parts.push(`${this.reservationDetails.total_infants} infants`);
    }
    return `Guests: ${  parts.join(' | ')}`;
  }

  async selectedAddress(addressId: number) {
    const addressResponse = await this.client.getMoreDetailAddresses(true);
    return addressResponse.find(ad => ad.id === addressId);
  }

  async updateAutomaticBooking() {
    try {
      while (!this.automaticBookingSettings) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      const addressId = this.dialogParams?.addressId || this.navCtrl.getParam('addressId');
      const address = await this.selectedAddress(addressId);
      const automaticBookingEnabled = address?.address_automatic_booking_setting?.automatic_booking_enabled;
      if (!automaticBookingEnabled) {
        const successParams = {
          header: 'Please Enable Auto-Schedule for This Property',
          body: 'Please enable auto-schedule for this property to turn it on for this reservation.',
          buttonText: 'View Automations',
          buttonRoute: 'automations/summary'
        };
        return this.rightSidePanelService.navigateTo('success', successParams, SuccessPage);
      }
      const value = !this.detail?.automatic_booking_settings[0]?.is_automatic_booking_enabled;
      await this.guestReservations.toggleAutomaticBookingForReservation(this.guestReservation.id, value);
      this.utils.showSuccess('Auto-schedule updated successfully');
    } catch (err) {
      this.utils.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  toggleSourceData(itemId: string) {
    this.expandedSourceData[itemId] = !this.expandedSourceData[itemId];
  }

  editCustomFields() {
    const instance = { ...this.guestReservation, custom_fields: this.detail?.custom_fields };
    this.rightSidePanelService.navigateTo('custom-fields', { modelType: 'GuestReservation', instance }, CustomFieldsPage);
  }

  addCustomField() {
    this.rightSidePanelService.navigateTo('integration/add-custom-field', { modelType: 'GuestReservation' }, AddCustomFieldPage);
  }

  isSourceDataExpanded(itemId: string): boolean {
    return this.expandedSourceData[itemId] || false;
  }

  // Airbnb "accept" is a pre-approval: the guest gets a 24h window to book at
  // the inquiry's dates/price. Nothing is confirmed or charged here.
  async preapproveInquiry(): Promise<void> {
    // Demo sessions only get the "create an account" prompt — without this
    // guard the interceptor swallows the POST with an empty 200 and the
    // success path fires on the shared demo account.
    if (this.demoBlock.block()) {
      return;
    }
    const inquiry = this.inquiryDetail;
    if (!inquiry || this.inquiryBusy) {
      return;
    }
    this.inquiryBusy = true;
    try {
      await firstValueFrom(this.inquiries.preapproveInquiry(inquiry.id));
      this.inquiryBusy = false;
      this.utils.showSuccess(
        `Pre-approval sent — ${this.firstName(inquiry.guest.name)} can book these dates within 24 hours`
      );
      this.closeInquiry();
    } catch (_err) {
      this.inquiryBusy = false;
      this.utils.showError('Could not send the pre-approval. Please try again.');
    }
  }

  // Tidy "accept" charges the guest's saved card and confirms the stay in one
  // call (POST /inquiries/:id/approve), so it always confirms first.
  async approveInquiryWithCharge(): Promise<void> {
    // Same reason as preapproveInquiry: a demo session must only get the
    // "create an account" prompt, never the confirm-and-charge flow.
    if (this.demoBlock.block()) {
      return;
    }
    const inquiry = this.inquiryDetail;
    if (!inquiry || this.inquiryBusy) {
      return;
    }
    const currency = inquiry.pricing?.currency ?? 'USD';
    const totalCents = inquiry.total_amount_cents ?? inquiry.pricing?.total_cents ?? 0;
    const total = this.chargeMoney(totalCents, currency);
    const guestFirstName = this.firstName(inquiry.guest.name);
    const nightsCents = inquiry.pricing?.subtotal_cents;
    const depositCents = inquiry.pricing?.security_deposit_cents;
    const breakdownNote =
      nightsCents !== undefined && depositCents !== undefined
        ? ` (${this.chargeMoney(nightsCents, currency)} + ${this.chargeMoney(depositCents, currency)} security deposit)`
        : '';
    // The detail fetch includes the saved card, so the final confirmation can
    // say exactly which card is about to be charged.
    const cardNote = inquiry.card_last4 ? ` ending in ${inquiry.card_last4}` : '';
    const confirmParams: ConfirmPageParamsModel = {
      title: new TranslationPipe().transform('Accept & Charge?'),
      body: new TranslationPipe().transform(
        `This charges ${guestFirstName}'s card${cardNote} ${total}${breakdownNote} and confirms the stay.`
      ),
      backText: new TranslationPipe().transform('Go Back'),
      confirmText: new TranslationPipe().transform('Accept & Charge'),
      confirmAction: () => this.confirmApproveInquiry(inquiry, total, guestFirstName),
    };
    const confirmationModal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: confirmParams,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await confirmationModal.present();
  }

  private async confirmApproveInquiry(inquiry: Inquiry, total: string, guestFirstName: string): Promise<void> {
    if (this.inquiryBusy) {
      return;
    }
    this.inquiryBusy = true;
    try {
      await firstValueFrom(this.inquiries.approveInquiry(inquiry.id));
      this.inquiryBusy = false;
      this.modalController.dismiss();
      this.utils.showSuccess(`Booked — ${guestFirstName} was charged ${total}`);
      this.closeInquiry();
    } catch (err) {
      this.inquiryBusy = false;
      this.modalController.dismiss();
      await this.handleApproveInquiryError(inquiry, err);
    }
  }

  private async handleApproveInquiryError(inquiry: Inquiry, err: unknown): Promise<void> {
    // ErrorInterceptor discards the HttpErrorResponse and re-throws a
    // flattened { code, message, err } — so branch on the backend's
    // status-mirroring `code`; `status` never reaches this handler.
    const { code, message, reason, minStay } = parseInquiryActionError(err);
    switch (code) {
      case 402:
        // Card refused — rolled back, guest emailed a fix-card link, approve
        // stays available. Refresh so the charge-failed state shows.
        this.utils.showError(
          `The charge failed — ${cardFailureReasonLabel(reason)}. The guest has been emailed a link to fix their card, and you can retry the charge.`,
          10000
        );
        await this.refreshInquiryDetail(inquiry.id);
        break;
      case 422: {
        // Dates blocked — nothing charged; decline is the only action left.
        // The backend says why in data.reason (dates_unavailable |
        // min_stay_not_met | night_unpriced), so tell the host the blocker.
        this.utils.showError(approveBlockedMessage(reason, minStay), 10000);
        await this.refreshInquiryDetail(inquiry.id);
        break;
      }
      case 403:
        // Resolved/expired elsewhere — refresh so the stale actions disappear.
        this.utils.showError(message || 'This inquiry can no longer be accepted.', 10000);
        await this.refreshInquiryDetail(inquiry.id);
        break;
      default:
        this.utils.showError(message || 'Could not accept the inquiry. Please try again.');
    }
  }

  private async refreshInquiryDetail(id: number): Promise<void> {
    try {
      this.inquiryDetail = await firstValueFrom(this.inquiries.getInquiryById(id));
      this.detail = this.inquiryDetail;
      this.inquiryView = this.buildInquiryView(this.inquiryDetail);
    } catch (_err) {
      // Keep the stale view rather than blank the modal.
    }
  }

  // Rendered as a text link rather than an anchor so it reads as one of the
  // page's links instead of a control competing with Decline / Pre-approve.
  openInquiryPlatform(): void {
    const url = this.inquiryView?.platformUrl;
    if (url) {
      window.open(url, '_blank', 'noopener');
    }
  }

  openInquiryDecline(): void {
    if (this.demoBlock.block()) {
      return;
    }
    if (this.inquiryBusy) {
      return;
    }
    this.decliningInquiry = this.inquiryDetail;
  }

  closeInquiryDecline(): void {
    if (this.inquiryBusy) {
      return;
    }
    this.decliningInquiry = null;
  }

  async submitInquiryDecline(payload: InquiryDeclineSubmit): Promise<void> {
    if (this.demoBlock.block()) {
      return;
    }
    const inquiry = this.decliningInquiry;
    if (!inquiry || this.inquiryBusy) {
      return;
    }
    this.inquiryBusy = true;
    try {
      await firstValueFrom(
        this.inquiries.declineInquiry(
          inquiry.id,
          payload.declineReason,
          payload.messageToGuest,
          payload.messageToPlatform
        )
      );
      this.inquiryBusy = false;
      this.decliningInquiry = null;
      this.utils.showSuccess('Inquiry declined');
      this.closeInquiry();
    } catch (err) {
      this.inquiryBusy = false;
      // Same flattened interceptor shape as approve — surface the backend's
      // message (e.g. a rejected decline payload) when sent.
      const { message } = parseInquiryActionError(err);
      this.utils.showError(message || 'Could not decline the inquiry. Please try again.');
    }
  }

  // After acting, the inquiry no longer needs this modal. On mobile this is a
  // real page, so return to whichever list opened it — Schedule and Inquiries
  // both route here, and hard-coding 'schedule' stranded anyone who arrived
  // from the Inquiries list on a page they had not been on.
  private closeInquiry(): void {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateBack(this.dialogParams?.returnTo || 'schedule');
    }
  }

  private buildInquiryView(inquiry: Inquiry): InquiryViewModel {
    const nights =
      this.nightsBetween(inquiry.check_in_date, inquiry.check_out_date) ??
      inquiry.pricing?.nights ??
      null;
    const nightsLabel = this.nightsLabel(nights);
    const datesKnown = !!(inquiry.check_in_date && inquiry.check_out_date);
    const needsResponse = inquiry.status === 'pending';
    // Pre-approval opens a 24h booking window and approve charges the card
    // outright, so never offer either for a stay this screen cannot show.
    const canPreapprove =
      inquiry.available_actions.includes('preapprove') && datesKnown;
    const canApprove =
      inquiry.available_actions.includes('approve') && datesKnown;
    const canDecline = inquiry.available_actions.includes('decline');
    const chargeFailed = !!inquiry.pending_requirements.card;
    const currency = inquiry.pricing?.currency;
    const chargeCents = inquiry.total_amount_cents ?? inquiry.pricing?.total_cents ?? 0;
    const chargeLabel = this.chargeMoney(chargeCents, currency ?? 'USD');
    const isTidy = inquiry.platform === 'tidy';
    // An inquiry the host can no longer act on badges as Expired — a lapsed
    // pre-approval is never written back at all, and the pending sweep is
    // async, so status alone lags reality.
    const presentation = inquiryStatusPresentation(inquiry);
    return {
      guestName: inquiry.guest.name,
      initials: this.initials(inquiry.guest.name),
      subtitle: [`${inquiry.source_name} inquiry`, nightsLabel].filter(Boolean).join(' · '),
      statusLabel: presentation.label,
      statusTone: presentation.tone,
      // Why it expired, when it did — a lapsed pre-approval is a missed
      // booking, an unanswered one is a missed reply. Suppressed on live
      // statuses: their copy is the expiresLabel chip right below.
      statusDetail: presentation.status === 'expired' ? presentation.detail : null,
      // The charge-failed banner replaces the waiting-for-a-reply nudge. Empty
      // available_actions means the host can no longer act (e.g. expired but
      // not yet swept to status 'expired'), so don't nudge them to reply then.
      needsResponse: needsResponse && !chargeFailed && (canPreapprove || canApprove || canDecline),
      bannerTitle: `${this.firstName(inquiry.guest.name)} is waiting for a reply`,
      bannerHint: canApprove
        ? 'Accept & charge or decline — the card is only charged when you accept'
        : 'Pre-approve or decline — quick replies protect your response rate',
      chargeFailedNote: chargeFailed
        ? `Approved — guest's card failed (${cardFailureReasonLabel(inquiry.pending_requirements.card?.reason)}). They've been emailed a link to fix it. You can retry the charge.`
        : null,
      checkIn: this.stayEdge(inquiry.check_in_date),
      checkOut: this.stayEdge(inquiry.check_out_date),
      nightsLabel,
      guestsLabel: inquiry.total_guests
        ? `${inquiry.total_guests} guest${inquiry.total_guests === 1 ? '' : 's'}`
        : null,
      propertyLine: inquiry.property_location
        ? `${inquiry.property_name} · ${inquiry.property_location}`
        : inquiry.property_name,
      // An effectively-expired row already wears the Expired badge — an
      // "Expiring soon" chip beside it would contradict it.
      expiresLabel:
        presentation.status === 'expired' ? null : this.inquiryExpiresLabel(inquiry.expires_at),
      message: inquiry.message || null,
      payoutTitle: isTidy ? 'Booking total' : 'Potential payout',
      // The detail fetch is the one place the card rides along (the list skips
      // the per-row billing read), so surface the last4 where the host decides.
      payoutHint: isTidy
        ? inquiry.card_last4
          ? `Charged to the guest's card •• ${inquiry.card_last4} when you accept`
          : "Charged to the guest's card when you accept"
        : 'Nothing is charged unless the guest books',
      breakdownRows: currency
        ? (inquiry.pricing?.breakdown ?? []).map((item) => ({
            label: item.description,
            value: this.money(item.amount_cents, currency),
          }))
        : [],
      totalLabel:
        currency && inquiry.pricing
          ? this.money(inquiry.pricing.total_cents, currency)
          : null,
      canPreapprove,
      canApprove,
      acceptLabel: chargeFailed ? `Retry charge ${chargeLabel}` : `Accept & Charge ${chargeLabel}`,
      canDecline,
      canAct: canPreapprove || canApprove || canDecline,
      platformUrl: inquiry.platform_url ?? null,
      platformLinkText: `Open in ${inquiry.source_name}`,
    };
  }

  private buildReservationView(): ReservationViewModel {
    const guest = this.primaryGuest;
    const guestName =
      guest?.name ||
      this.guestReservation?.guest_name ||
      this.detail?.guest_name ||
      'Guest';
    const checkIn = this.guestReservation?.check_in_date ?? '';
    const checkOut = this.guestReservation?.check_out_date ?? '';
    const nights = this.nightsBetween(checkIn, checkOut);
    const addedByYou =
      isHostAddedSource(this.guestReservation?.guest_reservation_source_type) ||
      isHostAddedSource({ slug: this.detail?.source_type_slug, format: this.detail?.source_type_format });
    const sourceName = addedByYou
      ? null
      : this.detail?.source_type_name || this.detail?.source_name || null;
    const totalGuests = this.reservationDetails?.total_guests;
    const currency = reservationCurrency(this.guestReservation) ?? reservationCurrency(this.detail);
    // The details response carries the whole feed copy; the opener's may not.
    const breakdown = addedByYou
      ? null
      : channelBreakdownView(this.detail?.source_data ?? this.guestReservation?.source_data, sourceName, currency);
    return {
      guestName,
      initials: this.initials(guestName),
      subtitle: [sourceName || 'Added manually', this.nightsLabel(nights)]
        .filter(Boolean)
        .join(' · '),
      checkIn: this.stayEdge(checkIn),
      checkOut: this.stayEdge(checkOut),
      nightsLabel: this.nightsLabel(nights),
      guestsLabel: totalGuests
        ? `${totalGuests} guest${totalGuests === 1 ? '' : 's'}`
        : null,
      guestBreakdown: this.buildGuestBreakdown(),
      guestRows: this.guests.map((item) => ({
        id: item.id,
        name: item.name || 'Guest',
        initials: this.initials(item.name || 'Guest'),
        email: item.email || null,
        phone: item.phone || null,
        isBooker: !!(item.is_booker ?? item.isBooker),
      })),
      addedByYou,
      sourceName,
      // The details response is fresh; the opener's copy may predate it or,
      // from some entry points, not carry the flag at all.
      isHold: !!(this.detail?.is_tentative ?? this.guestReservation?.is_tentative),
      autoScheduleStatuses: this.getUniqueStatuses(this.detail?.automatic_booking_statuses)
        .map((status: any) => status?.text)
        .filter(Boolean),
      // The breakdown's own totals, so the lines always add up to what shows.
      priceLabel: breakdown?.totalValue ?? formatCents(
        reservationPriceCents(this.guestReservation) ?? reservationPriceCents(this.detail),
        currency
      ),
      payoutLabel: breakdown?.payoutValue ?? formatCents(
        reservationHostPayoutCents(this.guestReservation) ?? reservationHostPayoutCents(this.detail),
        currency
      ),
      breakdown,
      canEditPrice: addedByYou,
      deleteLabel: this.isTidyReservation ? 'Cancel Reservation' : 'Delete Reservation',
    };
  }

  private stayEdge(ymd: string | null): ReservationStayEdge {
    const day = LuxonDateTime.fromISO(ymd ?? '');
    return {
      weekday: day.isValid ? day.toFormat('ccc') : '',
      date: day.isValid ? day.toFormat('MMM d') : '—',
    };
  }

  // Secondary guest counts (adults/children/pets/infants) as a compact line —
  // the headline guest count already rides a pill.
  private buildGuestBreakdown(): string | null {
    const details = this.reservationDetails;
    if (!details) {
      return null;
    }
    const parts: string[] = [];
    if (details.total_adults) { parts.push(`${details.total_adults} adults`); }
    if (details.total_children) { parts.push(`${details.total_children} children`); }
    if (details.total_infants) { parts.push(`${details.total_infants} infants`); }
    if (details.total_pets) { parts.push(`${details.total_pets} pets`); }
    return parts.length ? parts.join(' · ') : null;
  }

  private inquiryExpiresLabel(expiresAt?: string): string | null {
    if (!expiresAt) {
      return null;
    }
    const hours = Math.round((new Date(expiresAt).getTime() - Date.now()) / 3_600_000);
    if (hours <= 0) {
      return 'Expiring soon';
    }
    if (hours < 24) {
      return `Expires in ${hours}h`;
    }
    return `Expires in ${Math.round(hours / 24)}d`;
  }

  // Channel inquiries carry no pricing payload, so nights come from the dates.
  // null when the stay length can't be known — a dateless inquiry is a normal
  // state, and guessing "1 night" misstates the booking.
  private nightsBetween(checkIn: string | null, checkOut: string | null): number | null {
    if (!checkIn || !checkOut) {
      return null;
    }
    const nights = LuxonDateTime.fromISO(checkOut).diff(LuxonDateTime.fromISO(checkIn), 'days').days;
    return Number.isFinite(nights) && nights > 0 ? Math.round(nights) : null;
  }

  private nightsLabel(nights: number | null): string | null {
    return nights === null ? null : `${nights} night${nights === 1 ? '' : 's'}`;
  }

  private money(cents: number, currency: string): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  // Exact amount for charge copy (labels, confirm, toast): whole dollars stay
  // compact ("$450"), fractional amounts keep their cents ("$450.50").
  private chargeMoney(cents: number, currency: string): string {
    const wholeDollars = cents % 100 === 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: wholeDollars ? 0 : 2,
      maximumFractionDigits: wholeDollars ? 0 : 2,
    }).format(cents / 100);
  }

  private initials(name: string): string {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || '?';
  }

  private firstName(name: string): string {
    return name.trim().split(/\s+/)[0] || name;
  }

}
